import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import UserManagement from './components/UserManagement';
import CostLibrary from './components/CostLibrary';
import MasterDataManagement from './components/MasterDataManagement';
import LogoutScreen from './components/LogoutScreen';
import ProtectedRoute from './components/ProtectedRoute';
import Projects from './components/Projects';
import ProjectEstimates from './components/ProjectEstimates';
import EstimateDetail from './components/EstimateDetail';
import DocumentArchives from './components/DocumentArchives';
import { permissions } from './constants/permissions';

import { appColors } from './theme/colors';

// Create a custom theme with responsive design (palette aligned with Tailwind app colors)
const theme = createTheme({
  palette: {
    primary: {
      main: appColors.primary,
      light: '#60a5fa',
      dark: appColors.primaryDark,
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginTop: 0,
          marginBottom: 0,
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

// Component to handle initial routing
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return null; // Loading is handled in ProtectedRoute
  }

  const defaultAuthenticatedRoute = (() => {
    const granted = new Set(user?.permissions ?? []);
    if (granted.has(permissions.userManagement.view)) return '/users';
    if (granted.has(permissions.projects.view)) return '/projects';
    if (granted.has(permissions.costLibrary.view)) return '/costlibrary';
    if (granted.has(permissions.masterData.view)) return '/masterdata';
    if (granted.has(permissions.documentArchives.view)) return '/document-archives';
    return '/login';
  })();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to={defaultAuthenticatedRoute} replace /> : <LoginPage />} 
      />
      <Route
        path="/logout"
        element={<LogoutScreen />}
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredPermissions={[permissions.userManagement.view]}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/costlibrary"
        element={
          <ProtectedRoute requiredPermissions={[permissions.costLibrary.view]}>
            <CostLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/masterdata"
        element={
          <ProtectedRoute requiredPermissions={[permissions.masterData.view]}>
            <MasterDataManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-archives"
        element={
          <ProtectedRoute requiredPermissions={[permissions.documentArchives.view]}>
            <DocumentArchives />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute requiredPermissions={[permissions.projects.view]}>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/new"
        element={
          <ProtectedRoute requiredPermissions={[permissions.projects.create]}>
            <Projects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/edit"
        element={<Navigate to="/projects" replace />}
      />
      <Route
        path="/projects/:projectId/estimates"
        element={
          <ProtectedRoute requiredPermissions={[permissions.estimates.view]}>
            <ProjectEstimates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/estimates/:estimateId"
        element={
          <ProtectedRoute requiredPermissions={[permissions.estimates.view]}>
            <EstimateDetail />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to={defaultAuthenticatedRoute} replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;